import { useEffect, useRef, useState } from 'react';
import { useSession, type GeoMeta } from '../store/sessionStore';
import { MUSEUM_POINTS, validPosition } from './objects';
import PickerMap from './PickerMap';
import AddressSearch from './AddressSearch';
import { placeLine, reverseGeocode } from './reverseGeocode';

export const SOURCE_LABEL: Record<GeoMeta['source'], string> = { gps: 'GPS браузера', manual: 'Введено вручную', map: 'Выбрано на карте', object: 'Выбран объект', chat: 'Сообщено в чате' };
export default function LocationPanel() {
  const current = useSession((s) => s.agentGeoSet ? s.agentGeo : s.geo);
  const meta = useSession((s) => s.agentGeoMeta);
  const [lat, setLat] = useState(current?.lat.toString() ?? '');
  const [lon, setLon] = useState(current?.lon.toString() ?? '');
  const [selectedId, setSelectedId] = useState(() => meta?.source === 'object'
    ? MUSEUM_POINTS.find((point) => point.lat === current?.lat && point.lon === current?.lon)?.id ?? '' : '');
  const [selection, setSelection] = useState<Partial<GeoMeta>>(meta ?? { source: current ? 'gps' : 'manual' });
  const [error, setError] = useState('');
  const [locating, setLocating] = useState(false);
  useEffect(() => {
    if (meta?.source !== 'chat' || !current) return;
    cancelGps();
    setLat(String(current.lat)); setLon(String(current.lon)); setSelectedId('');
    setSelection(meta); setError('');
  }, [current, meta]);
  const active = useRef(true);
  const gpsRequest = useRef(0);
  function cancelGps(): void { gpsRequest.current += 1; setLocating(false); }
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const point = lat.trim() && lon.trim() && validPosition(Number(lat), Number(lon)) ? { lat: Number(lat), lon: Number(lon) } : null;
  function locate(): void {
    if (!navigator.geolocation) { setError('Браузер не поддерживает геопозицию.'); return; }
    const request = ++gpsRequest.current;
    setLocating(true); setError('');
    navigator.geolocation.getCurrentPosition((p) => {
      if (!active.current || request !== gpsRequest.current) return;
      setLocating(false); setLat(String(p.coords.latitude)); setLon(String(p.coords.longitude));
      setSelectedId('');
      setSelection({ source: 'gps', at: p.timestamp, accuracyM: p.coords.accuracy });
      // INV-PH-140: the GPS point gets its address (2GIS Geocoder); no answer — no name, as before
      void reverseGeocode(p.coords.latitude, p.coords.longitude).then((label) => {
        if (!label || !active.current || request !== gpsRequest.current) return;
        setSelection({ source: 'gps', at: p.timestamp, accuracyM: p.coords.accuracy, label });
      });
    }, (e) => {
      if (!active.current || request !== gpsRequest.current) return;
      setLocating(false); setError(e.code === e.PERMISSION_DENIED ? 'Доступ к геопозиции закрыт. Можно выбрать точку вручную.' : 'Не удалось определить положение. Попробуйте ещё раз или выберите точку.');
    }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
  }
  return <>
    <h2>Местоположение</h2>
    <p>Выбор будет передан гиду со следующим сообщением. Сам выбор сообщения не отправляет.</p>
    <p className="dev-muted">Сейчас: {current ? `${current.lat.toFixed(6)}, ${current.lon.toFixed(6)}` : 'не задано'}
      {meta && <> · {SOURCE_LABEL[meta.source]} · {new Date(meta.at).toLocaleTimeString('ru')}{meta.accuracyM !== null ? ` · точность ±${Math.round(meta.accuracyM)} м` : ''}{meta.label ? ` · ${meta.label}` : ''}</>}</p>
    {meta?.source === 'chat' && <p className="dev-muted">Это ориентир из вашего сообщения, а не точная GPS-позиция.</p>}
    <button className="btn" type="button" onClick={locate} disabled={locating}>{locating ? 'Определяю…' : 'Определить моё положение'}</button>
    <AddressSearch onPick={(item) => { cancelGps(); setSelectedId(''); setLat(item.lat.toFixed(6)); setLon(item.lon.toFixed(6)); setSelection({ source: 'manual', label: item.title }); setError(''); }} />
    <PickerMap point={point} onPick={(p) => { cancelGps(); setSelectedId(''); setLat(p.lat.toFixed(6)); setLon(p.lon.toFixed(6)); setSelection({ source: 'map' }); setError(''); }} />
    <label>Я у объекта<select aria-label="Объект местоположения" value={selectedId} onChange={(e) => {
      const p = MUSEUM_POINTS.find((p) => p.id === e.target.value);
      if (p) { cancelGps(); setSelectedId(p.id); setLat(String(p.lat)); setLon(String(p.lon)); setSelection({ source: 'object', label: p.label }); setError(''); }
    }}>
      <option value="">Выберите объект</option>{MUSEUM_POINTS.map((p) => <option key={p.id} value={p.id}>
        {p.label}{MUSEUM_POINTS.some((other) => other.id !== p.id && other.label === p.label) ? ` · №${p.id}` : ''}
      </option>)}
    </select></label>
    <form onSubmit={(e) => {
      e.preventDefault();
      if (!point) { setError('Введите широту от −90 до 90 и долготу от −180 до 180.'); return; }
      cancelGps(); setError(''); useSession.getState().setAgentGeo(point, selection);
    }}>
      <div className="location-coordinates">
        <label>Широта<input inputMode="decimal" value={lat} onChange={(e) => { cancelGps(); setSelectedId(''); setLat(e.target.value); setSelection({ source: 'manual' }); }} /></label>
        <label>Долгота<input inputMode="decimal" value={lon} onChange={(e) => { cancelGps(); setSelectedId(''); setLon(e.target.value); setSelection({ source: 'manual' }); }} /></label>
      </div>
      <p className="dev-muted">Выбор: {SOURCE_LABEL[selection.source ?? 'manual']}{selection.label ? ` · ${selection.source === 'gps' ? placeLine(selection.label, selection.accuracyM ?? null) : selection.label}` : ''}</p>
      {error && <p role="alert">{error}</p>}
      <div className="dev-controls"><button className="btn" type="submit">Использовать эту точку</button>
        <button className="btn btn--quiet" type="button" onClick={() => {
          cancelGps(); useSession.getState().setAgentGeo(null); setSelectedId(''); setLat(''); setLon(''); setSelection({ source: 'manual' }); setError('');
        }}>Сбросить положение</button></div>
    </form>
  </>;
}
